import { useSyncExternalStore } from 'react';

export type ThemePref = 'dark' | 'light' | 'system';
export type Theme = 'dark' | 'light';

const KEY = 'fnms-theme';
const media = window.matchMedia('(prefers-color-scheme: light)');
const listeners = new Set<() => void>();

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'system' ? v : 'dark';
  } catch {
    return 'dark';
  }
}

let pref: ThemePref = readPref();

function resolve(p: ThemePref): Theme {
  return p === 'system' ? (media.matches ? 'light' : 'dark') : p;
}

function apply() {
  document.documentElement.setAttribute('data-theme', resolve(pref));
  listeners.forEach((l) => l());
}

media.addEventListener('change', () => pref === 'system' && apply());

export function setThemePref(next: ThemePref) {
  pref = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* storage blocked: keep in memory */
  }
  apply();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Current preference and the resolved theme; re-renders when either changes. Dark is the default. */
export function useTheme(): { pref: ThemePref; theme: Theme } {
  const p = useSyncExternalStore(subscribe, () => pref);
  const theme = useSyncExternalStore(subscribe, () => resolve(pref));
  return { pref: p, theme };
}
